import React, { memo, useMemo, useState, useRef, useEffect, useCallback } from 'react';

import { Virtual, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import _debounce from 'lodash/debounce';

import { ReactComponent as AngleLeftSvg } from 'assets/angle-left.svg';
import { ReactComponent as AngleRightSvg } from 'assets/angle-right.svg';

import { SLIDER_SIZES } from './sliderSize';
import PortfolioItem from './portfolioItem';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/virtual';

import './style.scss';

const generateRandomIntegerNotInArray = (arr) => {
  // Convert the array to a Set for O(1) lookup times
  const numSet = new Set(arr);

  // Define the range for random integers
  const min = 0;
  const max = Math.max(...arr) + arr.length;

  let randomInt;

  // Generate random integers until we find one not in the array
  do {
    randomInt = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (numSet.has(randomInt));

  return randomInt;
};

const ProfileWork = memo(({ detail, isMobile }) => {
  const prevElRef = useRef(null);
  const nextElRef = useRef(null);
  const videoRefs = useRef([]);

  const [currentlyPlaying, setCurrentlyPlaying] = useState([]);
  const [realActiveIndex, setRealActiveIndex] = useState(-1);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const images = useMemo(() => {
    if (detail?.id) {
      let portfolio = [];
      if (detail?.shop && Array.isArray(detail?.barbers_portfolio)) {
        portfolio = []
          .concat(...detail.barbers_portfolio.map((item) => item?.images?.map((img) => ({ ...img, username: item.username })) || []))
          .sort((a, b) => a.order - b.order);
      } else {
        portfolio = detail.portfolio?.images.sort((a, b) => a.order - b.order) || [];
      }

      if (Array.isArray(portfolio) && portfolio.length > 0 && portfolio.length < 16) {
        while (portfolio.length < 21) {
          const ids = portfolio.map((item) => item.id);
          portfolio = [...portfolio, ...portfolio.map((item) => ({ ...item, id: generateRandomIntegerNotInArray(ids) }))];
        }
      }
      return [...portfolio];
    }
    return [];
  }, [detail?.id, detail?.shop, detail?.barbers_portfolio, detail?.portfolio]);

  useEffect(() => {
    videoRefs.current = Array(images.length)
      .fill()
      .map((_, i) => videoRefs.current[i] || React.createRef());
  }, [images.length]);

  const handlePlay = useCallback((index) => setCurrentlyPlaying([index]), []);

  const handlePause = useCallback((index) => setCurrentlyPlaying((list) => list.filter((item) => item !== index)), []);

  const onSwiper = useCallback((swiper) => {
    swiper.params.navigation.prevEl = prevElRef.current;
    swiper.params.navigation.nextEl = nextElRef.current;
    setTimeout(() => {
      swiper?.navigation?.update();
      swiper?.virtual?.update();
    }, 100);
  }, []);

  const setScrolling = useMemo(
    () =>
      _debounce((swiper) => {
        try {
          setRealActiveIndex(swiper?.realIndex);
        } catch (e) {
          console.error(e);
        } finally {
          setTimeout(() => {
            swiper?.navigation?.update();
            swiper?.virtual?.update();
          }, 100);
        }
      }, 400),
    []
  );

  const handleSlideChange = useCallback(
    (swiper) => {
      setScrolling?.cancel();
      swiper?.navigation?.update();
      swiper?.virtual?.update();
      setScrolling(swiper);
      setCurrentlyPlaying([]);
    },
    [setScrolling]
  );

  const slidesPerView = useMemo(() => {
    let slides = 4;

    if (width < 476) {
      let windowWidth = width;
      if (windowWidth < 300) {
        windowWidth = 299;
      }
      slides = SLIDER_SIZES[windowWidth];
    } else if (width < 476) {
      slides = 1.95;
    } else if (width < 500) {
      slides = 1.98;
    } else if (width < 550) {
      slides = 2;
    } else if (width < 600) {
      slides = 2.2;
    } else if (width < 650) {
      slides = 2.4;
    } else if (width < 850) {
      slides = 3;
    }
    return slides;
  }, [width]);

  const spaceBetween = useMemo(() => {
    let space = 11;
    if (width > 992 && width < 1116) {
      space = 6;
    }
    return space;
  }, [width]);

  const content = useMemo(() => {
    if (images.length < 1) {
      return <></>;
    }

    return (
      <Swiper
        init={true}
        loop={true}
        observer={true}
        observeParents={true}
        parallax={true}
        initialSlide={0}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        centeredSlides={isMobile}
        className={isMobile ? 'work-swiper' : 'desktop-swiper work-swiper'}
        navigation={{
          prevEl: prevElRef.current,
          nextEl: nextElRef.current,
        }}
        breakpoints={{
          992: {
            navigation: {
              prevEl: '.portfolio-image-prev',
              nextEl: '.portfolio-image-next',
            },
            grabCursor: false,
            centeredSlides: false,
            allowTouchMove: false,
          },
        }}
        virtual={{ enabled: true }}
        onSlideChange={handleSlideChange}
        onSwiper={onSwiper}
        modules={[Virtual, Navigation]}>
        {images?.map((item, index) => (
          <SwiperSlide key={item.id} virtualIndex={index} className='work-slide'>
            <PortfolioItem
              {...item}
              realActiveIndex={realActiveIndex}
              index={index}
              videoRef={videoRefs.current[index]}
              isMobile={isMobile}
              currentlyPlaying={currentlyPlaying}
              handlePlay={handlePlay}
              handlePause={handlePause}
            />
          </SwiperSlide>
        ))}
        <If condition={!isMobile}>
          <div className='navigation'>
            <div className='portfolio-image-prev' ref={prevElRef}>
              <AngleLeftSvg />
            </div>
            <div className='portfolio-image-next' ref={nextElRef}>
              <AngleRightSvg />
            </div>
          </div>
        </If>
      </Swiper>
    );
  }, [currentlyPlaying, handlePause, handlePlay, handleSlideChange, images, isMobile, onSwiper, realActiveIndex, slidesPerView, spaceBetween]);

  return (
    <div className='container barber-profile-work'>
      <p className='text-2xl semi-bold'>Work</p>
      <Choose>
        <When condition={images.length}>{content}</When>
        <Otherwise>
          <p className='text-xl mt-8'>This barber has not uploaded his work yet</p>
        </Otherwise>
      </Choose>
    </div>
  );
});

export default ProfileWork;
